import { useState, useCallback } from "react";
import { galleryImages, galleryCategories, GalleryImage } from "@/mocks/gallery";

const heightMap: Record<string, string> = {
  tall: "h-72",
  medium: "h-56",
  short: "h-44",
};

interface LightboxState {
  index: number;
  items: GalleryImage[];
}

export default function GalleryGrid() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  const filtered =
    activeCategory === "All"
      ? galleryImages
      : galleryImages.filter((g) => g.category === activeCategory);

  const openLightbox = useCallback((index: number) => {
    setLightbox({ index, items: filtered });
  }, [filtered]);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  const goNext = useCallback(() => {
    if (!lightbox) return;
    setLightbox((prev) =>
      prev ? { ...prev, index: (prev.index + 1) % prev.items.length } : null
    );
  }, [lightbox]);

  const goPrev = useCallback(() => {
    if (!lightbox) return;
    setLightbox((prev) =>
      prev
        ? { ...prev, index: (prev.index - 1 + prev.items.length) % prev.items.length }
        : null
    );
  }, [lightbox]);

  const current = lightbox ? lightbox.items[lightbox.index] : null;

  return (
    <section className="bg-brand-beige py-16 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {galleryCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`whitespace-nowrap font-display text-xs tracking-widest uppercase px-6 py-2.5 rounded-full border transition-all duration-300 cursor-pointer ${
                activeCategory === cat
                  ? "bg-brand-brown border-brand-brown text-brand-cream"
                  : "border-brand-brown/30 text-brand-brown hover:border-brand-gold hover:text-brand-gold"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Masonry Grid */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
          {filtered.map((item, idx) => (
            <div
              key={item.id}
              className="break-inside-avoid group relative overflow-hidden rounded-lg cursor-pointer"
              onClick={() => openLightbox(idx)}
            >
              <div className={`w-full ${heightMap[item.height] || "h-56"} overflow-hidden`}>
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-brand-darkbrown/0 group-hover:bg-brand-darkbrown/55 transition-all duration-400 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-center px-4">
                  <div className="w-11 h-11 flex items-center justify-center mx-auto mb-3 border-2 border-white rounded-full">
                    <i className="ri-zoom-in-line text-white text-xl" />
                  </div>
                  <p className="font-serif text-white text-base font-bold">{item.title}</p>
                </div>
              </div>
              {/* Category Tag */}
              <div className="absolute top-3 left-3 bg-brand-gold text-brand-darkbrown font-display text-[9px] tracking-widest uppercase px-2.5 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                {item.category}
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="font-sans text-brand-muted text-sm">No images in this category yet.</p>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && current && (
        <div
          className="fixed inset-0 z-50 bg-brand-darkbrown/96 flex items-center justify-center p-4 md:p-8"
          onClick={closeLightbox}
        >
          <div
            className="relative max-w-5xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={closeLightbox}
              className="absolute -top-11 right-0 w-9 h-9 flex items-center justify-center text-white/70 hover:text-white cursor-pointer transition-colors duration-200"
            >
              <i className="ri-close-line text-2xl" />
            </button>

            {/* Counter */}
            <div className="absolute -top-11 left-0 font-display text-xs tracking-widest text-white/50 uppercase flex items-center gap-2">
              <span>{lightbox.index + 1}</span>
              <span>/</span>
              <span>{lightbox.items.length}</span>
            </div>

            {/* Image */}
            <div className="w-full h-[65vh] rounded-lg overflow-hidden relative">
              <img
                src={current.image}
                alt={current.title}
                className="w-full h-full object-contain bg-brand-darkbrown/60"
              />
            </div>

            {/* Caption */}
            <div className="mt-4 text-center">
              <p className="font-serif text-xl text-brand-cream">{current.title}</p>
              <p className="font-display text-xs text-brand-gold tracking-widest uppercase mt-1">{current.category}</p>
            </div>

            {/* Prev Button */}
            <button
              onClick={goPrev}
              className="absolute left-3 md:left-5 top-1/2 -translate-y-8 w-10 h-10 flex items-center justify-center rounded-full border border-white/30 text-white/70 hover:text-white hover:border-white/70 hover:bg-white/10 cursor-pointer transition-all duration-200"
            >
              <i className="ri-arrow-left-line text-xl" />
            </button>

            {/* Next Button */}
            <button
              onClick={goNext}
              className="absolute right-3 md:right-5 top-1/2 -translate-y-8 w-10 h-10 flex items-center justify-center rounded-full border border-white/30 text-white/70 hover:text-white hover:border-white/70 hover:bg-white/10 cursor-pointer transition-all duration-200"
            >
              <i className="ri-arrow-right-line text-xl" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
